const LARGE_PRINT_AREA = 576;
const LARGE_PRINT_DISCOUNT = 0.08;
const LARGE_PRINT_DIVISOR = 1 + LARGE_PRINT_DISCOUNT;

// These are fallbacks for tests and for a temporarily incomplete API record.
// Normal production pricing uses each paper record's live `info` values.
const PAPER_DETAILS = {
  "epson hot press bright": { rate: 0.19, minimum: 16, maxWidth: 60, maxWidthCm: 150 },
  "epson cold press bright": { rate: 0.19, minimum: 16, maxWidth: 60, maxWidthCm: 150 },
  "moab lasal photo matte": { rate: 0.16, minimum: 15, maxWidth: 50, maxWidthCm: 125 },
  "hahnemuhle photo rag 308": { rate: 0.21, minimum: 18, maxWidth: 44, maxWidthCm: 125 },
  "hahnemuhle agave": { rate: 0.24, minimum: 20, maxWidth: 24, maxWidthCm: 61 },
  "epson premium luster": { rate: 0.16, minimum: 15, maxWidth: 60, maxWidthCm: 150 },
  "epson legacy platine": { rate: 0.21, minimum: 18, maxWidth: 44, maxWidthCm: 110 },
  "hahnemuhle photo rag baryta": { rate: 0.24, minimum: 20, maxWidth: 64, maxWidthCm: 160 },
  "epson metallic glossy": { rate: 0.19, minimum: 16, maxWidth: 44, maxWidthCm: 110 },
  "epson premium glossy": { rate: 0.21, minimum: 18, maxWidth: 44, maxWidthCm: 110 },
};

const MOUNTING_DETAILS = {
  foamBoard: {
    displayName: "Archival Foam Board",
    ratePerSqFt: 10.45,
    fixedFee: 10,
    minimumPrice: 20,
    minWidth: 11,
    minHeight: 14,
    maxWidth: 30,
    maxHeight: 40,
  },
  ultraThreeSixteenths: {
    displayName: 'UltraBoard (3/16")',
    ratePerSqFt: 10.45,
    fixedFee: 10,
    minimumPrice: 20,
    minWidth: 11,
    minHeight: 14,
    maxWidth: 48,
    maxHeight: 72,
  },
  ultraHalf: {
    displayName: 'UltraBoard (1/2")',
    ratePerSqFt: 13.9,
    fixedFee: 10,
    minimumPrice: 25,
    minWidth: 11,
    minHeight: 14,
    maxWidth: 48,
    maxHeight: 72,
  },
  dibondFloat: {
    displayName: "Dibond Float Mount",
    minimumPrice: 178,
    minWidth: 16,
    minHeight: 20,
    maxWidth: 59,
    maxHeight: 119,
  },
};

function normalizeKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[ü]/g, "u")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

function normalizePaperName(name) {
  if (normalizeKey(name) === "epson legacy baryta") {
    return "Epson Premium Glossy";
  }
  return name;
}

const DIBOND_COMPATIBLE_PAPERS = new Set([
  "epson premium glossy",
  "epson metallic glossy",
]);

function isDibondCompatiblePaper(paper) {
  const name = normalizePaperName(
    paper && typeof paper === "object" ? paper.Papertype : paper
  );
  return DIBOND_COMPATIBLE_PAPERS.has(normalizeKey(name));
}

function getPaperDetails(paper) {
  const name = normalizePaperName(
    paper && typeof paper === "object" ? paper.Papertype : paper
  );
  return PAPER_DETAILS[normalizeKey(name)] || null;
}

function normalizePaperRecord(paper) {
  if (!paper || typeof paper !== "object") return paper;

  const normalizedName = normalizePaperName(paper.Papertype);
  const details = getPaperDetails(normalizedName);

  if (!details) {
    return { ...paper, Papertype: normalizedName };
  }

  return {
    ...paper,
    Papertype: normalizedName,
    info: {
      ...paper.info,
      filpaperTypePricename:
        Number(paper.info?.filpaperTypePricename) || details.rate,
      paperBasePrice: Number(paper.info?.paperBasePrice) || details.minimum,
      paperRollWidthInches:
        Number(paper.info?.paperRollWidthInches) || details.maxWidth,
      paperRollWidthCm:
        Number(paper.info?.paperRollWidthCm) || details.maxWidthCm,
    },
  };
}

function toInches(value, measurement) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return measurement === "cm"
    ? Number((number / 2.54).toFixed(10))
    : number;
}

function getPaperPriceRaw({ width, height, measurement, paper }) {
  const widthInches = toInches(width, measurement);
  const heightInches = toInches(height, measurement);
  const details = getPaperDetails(paper);

  if (widthInches <= 0 || heightInches <= 0 || !details) return 0;

  const rate =
    Number(paper?.info?.filpaperTypePricename) || Number(details.rate);
  const minimum =
    Number(paper?.info?.paperBasePrice) || Number(details.minimum);

  if (rate <= 0 || minimum <= 0) return 0;

  const area = widthInches * heightInches;
  const paperCost = area * rate;

  // This mirrors the established Tricera public calculator. At 576 square
  // inches it applies the calculator's large-print reduction; smaller work is
  // subject to the paper minimum plus the calculator's fixed $2 addition.
  if (area >= LARGE_PRINT_AREA) {
    return paperCost / LARGE_PRINT_DIVISOR;
  }

  return Math.max(paperCost, minimum) + 2;
}

function calculatePaperPrice(input) {
  const rawPrice = getPaperPriceRaw(input);
  return rawPrice > 0 ? Math.round(rawPrice) : 0;
}

function getBorderedPaperDimensions({ width, height, measurement, borders }) {
  const widthValue = Number(width);
  const heightValue = Number(height);
  const allowance = measurement === "cm" ? 2.54 : 1;
  const borderWidth =
    Number(borders?.Left || 0) + Number(borders?.Right || 0);
  const borderHeight =
    Number(borders?.Top || 0) + Number(borders?.Bottom || 0);

  return {
    width: Math.max(widthValue + allowance, widthValue + borderWidth),
    height: Math.max(heightValue + allowance, heightValue + borderHeight),
    measurement,
  };
}

function calculateBorderedPaperPrice({
  width,
  height,
  measurement,
  borders,
  paper,
}) {
  const paperDimensions = getBorderedPaperDimensions({
    width,
    height,
    measurement,
    borders,
  });

  return calculatePaperPrice({ ...paperDimensions, paper });
}

function paperFitsRoll({ width, height, measurement, borders, paper }) {
  const paperDimensions = getBorderedPaperDimensions({
    width,
    height,
    measurement,
    borders,
  });
  const details = getPaperDetails(paper);
  const maxRollWidth =
    measurement === "cm"
      ? Number(paper?.info?.paperRollWidthCm) || Number(details?.maxWidthCm)
      : Number(paper?.info?.paperRollWidthInches) || Number(details?.maxWidth);

  if (maxRollWidth <= 0) return true;

  // Roll media can be rotated. Only the shorter paper dimension must fit the
  // roll; the longer dimension advances along the roll.
  return (
    Math.min(paperDimensions.width, paperDimensions.height) <= maxRollWidth
  );
}

function getMountingKey(name) {
  const key = normalizeKey(name);
  if (key.includes("dibond float") || key.includes("alupanel")) return "dibondFloat";
  if (key.includes("1/2") && (key.includes("ultra") || key.includes("ultraboard"))) return "ultraHalf";
  if (key.includes("3/16") && (key.includes("ultra") || key.includes("ultraboard"))) return "ultraThreeSixteenths";
  if (key.includes("foam board")) return "foamBoard";
  return null;
}

function validateMountingSize(width, height, details) {
  const shortSide = Math.min(width, height);
  const longSide = Math.max(width, height);
  const minShort = Math.min(details.minWidth, details.minHeight);
  const minLong = Math.max(details.minWidth, details.minHeight);
  const maxShort = Math.min(details.maxWidth, details.maxHeight);
  const maxLong = Math.max(details.maxWidth, details.maxHeight);

  if (shortSide < minShort || longSide < minLong) {
    return `Minimum size for ${details.displayName} is ${details.minWidth}\" × ${details.minHeight}\"`;
  }
  if (shortSide > maxShort || longSide > maxLong) {
    return `Maximum size for ${details.displayName} is ${details.maxWidth}\" × ${details.maxHeight}\"`;
  }
  return "";
}

function calculateMountingPrice({
  width,
  height,
  measurement,
  mounting,
  paper,
  hardwareWidth = width,
  hardwareHeight = height,
}) {
  const widthInches = toInches(width, measurement);
  const heightInches = toInches(height, measurement);
  const hardwareWidthInches = toInches(hardwareWidth, measurement);
  const hardwareHeightInches = toInches(hardwareHeight, measurement);
  const mountingKey = getMountingKey(mounting);
  const details = MOUNTING_DETAILS[mountingKey];

  if (widthInches <= 0 || heightInches <= 0 || !details) {
    return { price: 0, total: 0, printPrice: 0, error: "" };
  }

  if (mountingKey === "dibondFloat" && !isDibondCompatiblePaper(paper)) {
    return {
      price: 0,
      total: 0,
      printPrice: 0,
      error:
        "Sorry, Dibond Float Mount is only compatible with Epson Premium Glossy or Epson Metallic Glossy.",
    };
  }

  const error = validateMountingSize(
    hardwareWidthInches,
    hardwareHeightInches,
    details
  );
  if (error) {
    return { price: 0, total: 0, printPrice: 0, error };
  }

  const area = widthInches * heightInches;
  const squareFeet = Math.max(1, Math.round(area / 144));
  const printPrice = calculatePaperPrice({
    width: widthInches,
    height: heightInches,
    measurement: "inch",
    paper,
  });

  if (printPrice <= 0) {
    return { price: 0, total: 0, printPrice: 0, error: "Select a paper to see pricing" };
  }

  if (mountingKey === "dibondFloat") {
    const laminateCost = squareFeet * 11.55 * 1.05;
    const panelCost = squareFeet * 15.5 * 1.05;
    const framePerimeter =
      (hardwareWidthInches - 6) * 2 + (hardwareHeightInches - 6) * 2;
    const frameLinearFeet = Math.ceil(framePerimeter / 12);
    const backFrameCost = frameLinearFeet * 9.25 * 1.05;
    const zBarLinearFeet = Math.max(
      1,
      Math.ceil((hardwareWidthInches - 9) / 12)
    );
    const zBarCost = zBarLinearFeet * 3.5 * 1.05;
    const materials = laminateCost + panelCost + backFrameCost + zBarCost;
    const embeddedPrintPrice = Math.round(area * 0.16);
    const allInPrice =
      materials + embeddedPrintPrice <= details.minimumPrice
        ? details.minimumPrice
        : Math.round(materials) + embeddedPrintPrice;

    return {
      price: allInPrice,
      total: Math.max(0, allInPrice - printPrice),
      printPrice,
      error: "",
    };
  }

  const mountingPrice = Math.max(
    details.minimumPrice,
    Math.round(squareFeet * details.ratePerSqFt + details.fixedFee)
  );

  return {
    price: mountingPrice + printPrice,
    total: mountingPrice,
    printPrice,
    error: "",
  };
}

export {
  LARGE_PRINT_AREA,
  LARGE_PRINT_DISCOUNT,
  MOUNTING_DETAILS,
  PAPER_DETAILS,
  calculateBorderedPaperPrice,
  calculateMountingPrice,
  calculatePaperPrice,
  getBorderedPaperDimensions,
  getPaperDetails,
  getPaperPriceRaw,
  isDibondCompatiblePaper,
  normalizePaperName,
  normalizePaperRecord,
  paperFitsRoll,
};
